import { useEffect, useState } from "react";
import { Button } from "./Button.tsx";
import { CheckIcon } from "./Icons.tsx";
import { showToast } from "./Toast.tsx";

/** "Copied" stays on the button this long. */
const COPIED_MS = 4000;

/**
 * One button that copies a message to the clipboard, then says "Copied" for a moment. The open spots
 * card on Home (H2) and the same message in a suggestion of AI Assistant (A1) use it. `onCopied`
 * runs after a copy that worked (for its analytics event).
 */
export function CopyMessageButton({
  text,
  label,
  onCopied,
  initialCopied = false,
}: {
  text: string;
  label: string;
  onCopied?: () => void;
  /** Storybook: show the copied state. */
  initialCopied?: boolean;
}) {
  const [copied, setCopied] = useState(initialCopied);
  useEffect(() => {
    if (!copied || initialCopied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied, initialCopied]);

  async function copy() {
    try {
      if (!navigator.clipboard) throw new Error("No clipboard");
      await navigator.clipboard.writeText(text);
    } catch {
      showToast("Your browser did not let Teamna copy the message. Try again.", { tone: "info" });
      return;
    }
    setCopied(true);
    onCopied?.();
  }

  return (
    <Button variant={copied ? "secondary" : "primary"} onClick={() => void copy()} aria-live="polite">
      {copied ? (
        <span className="flex items-center gap-1.5">
          <CheckIcon size={16} /> Copied
        </span>
      ) : (
        label
      )}
    </Button>
  );
}
